import { useCallback, useEffect, useRef } from 'react';

interface TextSelectionState {
  /** Get the currently selected text (reads from ref, no re-render) */
  getSelectedText: () => string;
  /** Programmatically clear selection state and hide the button */
  clearSelection: () => void;
}

/**
 * Hook to detect text selection within a container element and show
 * a floating "Give Feedback" button near the selection.
 *
 * This hook intentionally avoids React state for the selection data.
 * Any React re-render during a selection causes the browser to collapse
 * it. Instead, we manipulate a persistent DOM element directly.
 */
export function useTextSelection(
  containerRef: React.RefObject<HTMLElement | null>,
  onFeedback: (selectedText: string) => void,
  options: { enabled?: boolean } = {}
): TextSelectionState {
  const { enabled = true } = options;
  const selectedTextRef = useRef('');
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const hideButton = useCallback(() => {
    if (buttonRef.current) {
      buttonRef.current.style.display = 'none';
    }
    selectedTextRef.current = '';
  }, []);

  const getSelectedText = useCallback(() => selectedTextRef.current, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Create the button once and keep it in the DOM permanently
    let button = buttonRef.current;
    if (!button) {
      button = document.createElement('button');
      button.textContent = ' Give Feedback';
      button.dataset.selectionFeedbackButton = '';
      Object.assign(button.style, {
        display: 'none',
        position: 'absolute',
        zIndex: '300',
        transform: 'translateX(-50%)',
        padding: '4px 12px',
        fontSize: '12px',
        fontWeight: '500',
        fontFamily: 'inherit',
        color: '#fff',
        backgroundColor: 'var(--mantine-color-violet-6, #7c3aed)',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        whiteSpace: 'nowrap',
      });

      // SVG icon prepended
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '14');
      svg.setAttribute('height', '14');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      svg.innerHTML =
        '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="12" y1="8" x2="12" y2="12"/>';
      svg.style.verticalAlign = 'middle';
      svg.style.marginRight = '4px';
      button.prepend(svg);

      button.addEventListener('mousedown', (e) => {
        // Prevent the click from collapsing the selection
        e.preventDefault();
      });

      button.addEventListener('click', () => {
        const text = selectedTextRef.current;
        if (text) {
          onFeedback(text);
          hideButton();
          globalThis.getSelection()?.removeAllRanges();
        }
      });

      document.body.appendChild(button);
      buttonRef.current = button;
    }

    const handleMouseUp = () => {
      setTimeout(() => {
        const selection = globalThis.getSelection();
        if (!selection || selection.isCollapsed || !selection.rangeCount) {
          return;
        }

        const range = selection.getRangeAt(0);
        if (!container.contains(range.commonAncestorContainer)) {
          return;
        }

        const text = selection.toString().trim();
        if (text && button) {
          selectedTextRef.current = text;
          const rect = range.getBoundingClientRect();
          button.style.top = `${rect.bottom + globalThis.scrollY + 8}px`;
          button.style.left = `${rect.left + globalThis.scrollX + rect.width / 2}px`;
          button.style.display = 'inline-flex';
        }
      }, 10);
    };

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-selection-feedback-button]')) {
        return;
      }
      if (!container.contains(target)) {
        hideButton();
      }
    };

    // Also hide if user clicks inside container but not on a selection
    const handleContainerMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-selection-feedback-button]')) {
        return;
      }
      // A mousedown inside the container starts a new selection, hide the old button
      hideButton();
    };

    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousedown', handleContainerMouseDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousedown', handleContainerMouseDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [containerRef, hideButton, onFeedback, enabled]);

  // Cleanup the button on unmount
  useEffect(() => {
    return () => {
      if (buttonRef.current) {
        buttonRef.current.remove();
        buttonRef.current = null;
      }
    };
  }, []);

  return { getSelectedText, clearSelection: hideButton };
}
